import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { rbacAPI } from '../../services/api';
import { PERMISSION_MODULES, PORTAL_TYPES } from '../../rbac';

const EMPTY_ROLE = {
  name: '', display_name: '', description: '', portal_type: 'admin', default_permissions: {},
};

export default function SystemRoles() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState(PERMISSION_MODULES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ search: '', portal_type: '' });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ROLE);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [applyingDefaults, setApplyingDefaults] = useState(null);
  const [sort, setSort] = useState({ key: 'display_name', direction: 'asc' });

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [rolesRes, modsRes] = await Promise.all([
        rbacAPI.getRoles(filters),
        rbacAPI.getPermissionModules(),
      ]);
      setRoles(rolesRes.data?.roles || []);
      if (modsRes.data?.modules) setModules(modsRes.data.modules);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load roles');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null); setForm(EMPTY_ROLE); setSaveError(null); setShowModal(true);
  };

  const openEdit = (role) => {
    setEditing(role.id);
    setForm({
      name: role.name || '',
      display_name: role.display_name || '',
      description: role.description || '',
      portal_type: role.portal_type || 'admin',
      default_permissions: role.default_permissions || {},
    });
    setSaveError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true); setSaveError(null);
      if (editing) {
        await rbacAPI.updateRole(editing, form);
      } else {
        await rbacAPI.createRole(form);
      }
      setShowModal(false);
      load();
    } catch (e) {
      setSaveError(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await rbacAPI.deleteRole(id);
      setConfirmDelete(null);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
      setConfirmDelete(null);
    }
  };

  const handleApplyDefaults = async (role) => {
    try {
      setApplyingDefaults(role.id);
      const res = await rbacAPI.applyDefaults(role.id);
      alert(res.data?.message || 'Applied');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    } finally { setApplyingDefaults(null); }
  };

  const togglePermission = (module, action) => {
    setForm(prev => {
      const perms = { ...prev.default_permissions };
      const actions = [...(perms[module] || [])];
      const idx = actions.indexOf(action);
      if (idx >= 0) actions.splice(idx, 1); else actions.push(action);
      if (actions.length === 0) delete perms[module]; else perms[module] = actions;
      return { ...prev, default_permissions: perms };
    });
  };

  const toggleAllModule = (module, allActions) => {
    setForm(prev => {
      const perms = { ...prev.default_permissions };
      const current = perms[module] || [];
      if (current.length === allActions.length) {
        delete perms[module];
      } else {
        perms[module] = [...allActions];
      }
      return { ...prev, default_permissions: perms };
    });
  };

  const selectAllPermissions = () => {
    setForm(prev => {
      const perms = {};
      modules.forEach(m => { perms[m.module] = [...m.actions]; });
      return { ...prev, default_permissions: perms };
    });
  };

  const clearAllPermissions = () => {
    setForm(prev => ({ ...prev, default_permissions: {} }));
  };

  const countPermissions = (perms) => {
    if (!perms || typeof perms !== 'object') return 0;
    return Object.values(perms).reduce((sum, actions) => sum + (Array.isArray(actions) ? actions.length : 0), 0);
  };

  const visibleRoles = useMemo(() => [...roles].sort((a,b) => String(a[sort.key] ?? '').localeCompare(String(b[sort.key] ?? ''), 'en', { numeric:true, sensitivity:'base' }) * (sort.direction === 'asc' ? 1 : -1)), [roles, sort]);
  const SortHeader = ({column,children}) => <button type="button" className={`sys-sort ${sort.key===column?'active':''}`} onClick={()=>setSort(current=>({key:column,direction:current.key===column&&current.direction==='asc'?'desc':'asc'}))}>{children}<span>{sort.key===column?(sort.direction==='asc'?'↑':'↓'):'↕'}</span></button>;
  const exportCsv = () => { const q=value=>`"${String(value??'').replace(/"/g,'""')}"`; const rows=[['Role','Key','Portal','Permissions','Users','System'],...visibleRoles.map(role=>[role.display_name,role.name,role.portal_type,countPermissions(role.default_permissions),role.user_count||0,role.is_system?'Yes':'No'])]; const link=document.createElement('a');link.href=URL.createObjectURL(new Blob(['\ufeff',rows.map(row=>row.map(q).join(',')).join('\n')],{type:'text/csv;charset=utf-8'}));link.download='roles-permissions.csv';link.click();URL.revokeObjectURL(link.href); };

  const portalBadge = (type) => {
    const colors = { public: '#27ae60', citizen: '#2980b9', admin: '#8e44ad', taluka: '#d35400', system: '#c0392b' };
    return (
      <span style={{ background: colors[type] || '#888', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600 }}>
        {type}
      </span>
    );
  };

  return (
    <div className="sys-page">
      <div className="sys-page-header">
        <div>
          <h1>Roles & Permissions</h1>
          <p>Define roles, assign default permissions, and control portal access</p>
        </div>
        <button className="sys-btn sys-btn-primary" onClick={openCreate}>+ Create Role</button>
      </div>

      <div className="sys-filter-bar sys-toolbar">
        <input placeholder="Search roles…" value={filters.search} onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); }} />
        <select value={filters.portal_type} onChange={e => { setFilters(f => ({ ...f, portal_type: e.target.value })); }}>
          <option value="">All Portals</option>
          {PORTAL_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <button className="sys-btn sys-btn-outline" onClick={exportCsv}>↓ Export CSV</button>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}>Loading…</div>}
      {error && <div style={{ textAlign: 'center', padding: 40, color: '#c62828' }}>{error}</div>}

      {!loading && !error && (
        <div className="sys-card">
          <div className="sys-table-wrap">
            <table className="sys-table">
              <thead>
                <tr>
                  <th><SortHeader column="display_name">Role</SortHeader></th>
                  <th><SortHeader column="portal_type">Portal</SortHeader></th>
                  <th>Permissions</th>
                  <th><SortHeader column="user_count">Users</SortHeader></th>
                  <th>System</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRoles.map(role => (
                  <tr key={role.id}>
                    <td>
                      <strong>{role.display_name}</strong>
                      <br /><span style={{ fontSize: '0.78rem', color: '#888' }}>{role.name}</span>
                    </td>
                    <td>{portalBadge(role.portal_type)}</td>
                    <td>{countPermissions(role.default_permissions)} permissions</td>
                    <td>{role.user_count || 0}</td>
                    <td>{role.is_system ? '🔒' : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="sys-btn sys-btn-outline" style={{ padding: '4px 10px', fontSize: '0.78rem' }} onClick={() => openEdit(role)}>Edit</button>
                        <button className="sys-btn sys-btn-outline" style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                          onClick={() => handleApplyDefaults(role)}
                          disabled={applyingDefaults === role.id}>
                          {applyingDefaults === role.id ? '…' : 'Apply to Users'}
                        </button>
                        {!role.is_system && (
                          <button className="sys-btn sys-btn-outline" style={{ padding: '4px 10px', fontSize: '0.78rem', color: '#c62828', borderColor: '#c62828' }}
                            onClick={() => setConfirmDelete(role)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {roles.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#aaa' }}>No roles found</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Role Modal */}
      {showModal && (
        <div className="sys-modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="sys-modal" style={{ maxWidth: 820, maxHeight: '90vh', overflow: 'auto' }}>
            <h2>{editing ? 'Edit Role' : 'Create Role'}</h2>
            {saveError && <div style={{ color: '#c62828', marginBottom: 12, fontSize: '0.85rem' }}>{saveError}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="sys-form-group">
                <label>Role Name (slug) *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. custom_inspector" disabled={editing && roles.find(r => r.id === editing)?.is_system} />
                <small style={{ color: '#888' }}>Lowercase, letters, numbers, underscores</small>
              </div>
              <div className="sys-form-group">
                <label>Display Name *</label>
                <input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="e.g. Custom Inspector" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="sys-form-group">
                <label>Portal Type *</label>
                <select value={form.portal_type} onChange={e => setForm(f => ({ ...f, portal_type: e.target.value }))}
                  disabled={editing && roles.find(r => r.id === editing)?.is_system}>
                  {PORTAL_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="sys-form-group">
                <label>Description</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description" />
              </div>
            </div>

            {/* Permissions Matrix */}
            <div style={{ marginTop: 16, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ margin: 0 }}>Default Permissions</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="sys-btn sys-btn-outline" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={selectAllPermissions}>Select All</button>
                  <button className="sys-btn sys-btn-outline" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={clearAllPermissions}>Clear All</button>
                </div>
              </div>

              <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Module</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', width: 60 }}>View</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', width: 60 }}>Create</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', width: 60 }}>Update</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', width: 60 }}>Delete</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', width: 50 }}>All</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((mod, i) => {
                      const currentPerms = form.default_permissions[mod.module] || [];
                      const allChecked = mod.actions.every(a => currentPerms.includes(a));
                      return (
                        <tr key={mod.module} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>{mod.label}</td>
                          {['view', 'create', 'update', 'delete'].map(action => (
                            <td key={action} style={{ textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                              {mod.actions.includes(action) ? (
                                <input type="checkbox"
                                  checked={currentPerms.includes(action)}
                                  onChange={() => togglePermission(mod.module, action)}
                                  style={{ cursor: 'pointer', width: 16, height: 16 }} />
                              ) : <span style={{ color: '#ccc' }}>—</span>}
                            </td>
                          ))}
                          <td style={{ textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                            <input type="checkbox"
                              checked={allChecked}
                              onChange={() => toggleAllModule(mod.module, mod.actions)}
                              style={{ cursor: 'pointer', width: 16, height: 16 }} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#666' }}>
                {countPermissions(form.default_permissions)} permission(s) selected
              </div>
            </div>

            <div className="sys-form-actions" style={{ marginTop: 16 }}>
              <button className="sys-btn sys-btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="sys-btn sys-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : (editing ? 'Update Role' : 'Create Role')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div className="sys-modal-overlay" onClick={e => e.target === e.currentTarget && setConfirmDelete(null)}>
          <div className="sys-modal" style={{ maxWidth: 420 }}>
            <h2>Delete Role</h2>
            <p>Are you sure you want to delete <strong>{confirmDelete.display_name}</strong>?</p>
            {confirmDelete.user_count > 0 && (
              <p style={{ color: '#c62828' }}>This role has {confirmDelete.user_count} user(s) assigned. You must reassign them first.</p>
            )}
            <div className="sys-form-actions">
              <button className="sys-btn sys-btn-outline" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="sys-btn sys-btn-primary" style={{ background: '#c62828' }}
                onClick={() => handleDelete(confirmDelete.id)} disabled={confirmDelete.user_count > 0}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
